// AiLiveRegion — the one visually-hidden aria-live region for the assistant.
//
// Copied from `module/quotation/components/EnrollmentLiveRegion.tsx`, including
// its inline visually-hidden technique, so both surfaces behave identically for
// a screen-reader user.
//
// ─── IT ANNOUNCES TRANSITIONS, NOT CONTENT ─────────────────────────────────
// "Working on your question." / "Answer ready." / "Stopped." — never the answer
// body. Two reasons:
//
//  1. The log is an `<ol>`, deliberately NOT `role="log"`. That role carries an
//     implicit polite live region that announces every DOM addition — including
//     the pending bubble's elapsed counter re-rendering once a second, which
//     makes the panel unusable with a screen reader within about three seconds.
//     So announcements are made here, explicitly, once per transition.
//  2. Focus moves to the answer turn when it lands. A ~7s answer that is also
//     read aloud in full, uninterruptibly, at the moment it arrives is worse
//     than one the operator can read at their own pace with the copy button
//     right there.

export interface AiLiveRegionProps {
  /** Already-translated transition copy. `''` renders an empty region. */
  readonly message: string;
  /** `polite` for progress, `assertive` for failures. */
  readonly level?: 'polite' | 'assertive';
}

export function AiLiveRegion({ message, level = 'polite' }: AiLiveRegionProps): JSX.Element {
  return (
    <div
      data-testid="ai-live-region"
      role="status"
      aria-live={level}
      aria-atomic="true"
      className="absolute h-px w-px overflow-hidden whitespace-nowrap border-0 p-0"
      style={{ clip: 'rect(0, 0, 0, 0)', clipPath: 'inset(50%)' }}
    >
      {message}
    </div>
  );
}
